'use client';

import {
  dustEnvelope,
  DUST_VARIANTS,
  DUST_MAX_OPACITY,
  PEN_TIP_X,
  PEN_TIP_Y,
  PEN_OUTLINE_COLOR,
} from '../lib/penReveal';

// Outline band width as a fraction of the sprite's bounding box (objectBoundingBox
// units), so it stays proportional in both the generative (canvas px) and curated
// (viewBox unit) coordinate spaces.
const OUTLINE_RADIUS = 0.022;

/**
 * Luminance→alpha + force-white filter so the pen/dust jpg's black background
 * drops out, leaving a clean white silhouette. Each host SVG renders one in its
 * <defs> with a unique id and refers to it via `filterId`.
 *
 * With `outline`, a solid dark band (feMorphology dilate, not a blur) is laid
 * behind the silhouette so the white pen stays visible on light backgrounds.
 */
export function PenFilter({ id, outline = false }) {
  // Outline needs an expanded region so the dilated band isn't clipped, in
  // objectBoundingBox units so the band width tracks the sprite size.
  const region = outline
    ? { primitiveUnits: 'objectBoundingBox', x: '-30%', y: '-30%', width: '160%', height: '160%' }
    : {};
  return (
    <filter id={id} colorInterpolationFilters="sRGB" {...region}>
      <feColorMatrix type="luminanceToAlpha" result="alpha" />
      <feComponentTransfer in="alpha" result="pen">
        <feFuncR type="linear" slope="0" intercept="1" />
        <feFuncG type="linear" slope="0" intercept="1" />
        <feFuncB type="linear" slope="0" intercept="1" />
      </feComponentTransfer>
      {outline && (
        <>
          <feMorphology in="alpha" operator="dilate" radius={OUTLINE_RADIUS} result="spread" />
          <feFlood floodColor={PEN_OUTLINE_COLOR} result="flood" />
          <feComposite in="flood" in2="spread" operator="in" result="band" />
          <feMerge>
            <feMergeNode in="band" />
            <feMergeNode in="pen" />
          </feMerge>
        </>
      )}
    </filter>
  );
}

/** Chalk puffs the pen has left behind — each fades in, grows, and fades out. */
export function DustParticles({ particles, filterId }) {
  return particles.map((pt) => {
    const env = dustEnvelope(pt.age);
    if (env <= 0) return null;
    const size = pt.baseSize * (0.85 + pt.age * 0.45);
    return (
      <g key={pt.id} transform={`translate(${pt.x}, ${pt.y}) rotate(${pt.rotation})`}>
        <image
          href={DUST_VARIANTS[pt.variantIdx]}
          x={-size / 2}
          y={-size / 2}
          width={size}
          height={size}
          filter={`url(#${filterId})`}
          opacity={env * DUST_MAX_OPACITY}
          preserveAspectRatio="xMidYMid meet"
          style={{ pointerEvents: 'none' }}
        />
      </g>
    );
  });
}

/** The pen sprite, positioned and rotated so its tip sits at (x, y). */
export function PenSprite({ x, y, angle, size, filterId }) {
  return (
    <g transform={`translate(${x}, ${y}) rotate(${angle})`}>
      <image
        href="/tapestry/pen.jpg"
        x={-size * PEN_TIP_X}
        y={-size * PEN_TIP_Y}
        width={size}
        height={size}
        filter={`url(#${filterId})`}
        preserveAspectRatio="xMidYMid meet"
        style={{ pointerEvents: 'none' }}
      />
    </g>
  );
}
